import React from 'react';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import KenTextLabel from '../KenTextLabel';
import StatusErrorIcon from './assets/StatusErrorIcon.svg';
import StatusSuccessIcon from './assets/StatusSuccessIcon.svg';
import StatusWarningIcon from './assets/StatusWarningIcon.svg';
import { Typography } from '@material-ui/core';
import { GLOBAL } from '../../utils/constants';
import KenColors from '../../themes/KenColors';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  paper: {
    display: 'flex',
    border: `1px solid ${theme.palette.KenColors.neutral40}`,
  },

  selected: {
    color: theme.palette.KenColors.primary,
    backgroundColor: theme.palette.KenColors.neutral10,
  },
  toggleButton: {
    marginTop: '0 !important',
    '&$selected': {
      color: theme.palette.KenColors.primary,
      backgroundColor: theme.palette.KenColors.neutral10,
      border: `1px solid ${theme.palette.KenColors.primary} !important`,
    },
  },
  typoSupporting: {
    fontSize: '12px',
    color: `${theme.palette.KenColors.neutral100}`,
    marginTop: 4,
  },
  inputBorderWarning: {
    border: `1px solid ${theme.palette.KenColors.tertiaryYellow300}`,
    borderRadius: 3,
  },
  inputBorderError: {
    border: `1px solid ${theme.palette.KenColors.tertiaryRed400}`,
    borderRadius: 3,
  },
  inputBorderSuccess: {
    border: `1px solid ${theme.palette.KenColors.tertiaryGreen300}`,
    borderRadius: 3,
  },
  inputBorder: {
    border: `1px solid ${theme.palette.KenColors.neutral40}`,
    borderRadius: 3,
  },
}));

const StyledToggleButtonGroup = withStyles((theme) => ({
  grouped: {
    margin: '6px',
    padding: '8px 9px',
    border: `1px solid ${theme.palette.KenColors.neutral40}`,
    borderRadius: 4,
    color: theme.palette.KenColors.neutral60,
    '&:not(:first-child)': {
      borderRadius: 4,
      border: `1px solid ${theme.palette.KenColors.neutral40}`,
    },
    '&:first-child': {
      borderRadius: 4,
      border: `1px solid ${theme.palette.KenColors.neutral40}`,
      marginLeft: 0,
    },
  },
}))(ToggleButtonGroup);

export default function KenToggleButton(props) {
  const { t } = useTranslation();
  const {
    value,
    onChange,
    options = [],
    required,
    optionalLabel,
    label,
    size = 'small',
    onBlur,
    onFocus,
    autofocus,
    errors,
    touched,
    type,
    setFieldTouched,
    name,
  } = props;

  const classes = useStyles();
  const getErrorStatus = (type) => {
    let status, helpText;
    if (!touched) return;
    if (errors === GLOBAL.VALIDATIONS.REQ) {
      status = 'warning';
      helpText = (
        <span style={{ color: KenColors.tertiaryYellow400 }}>
          <img src={StatusWarningIcon} alt='warning'></img>{' '}
          {t('validations:standard_required')}
        </span>
      );
    } else if (errors) {
      status = 'error';
      helpText = (
        <span style={{ color: KenColors.tertiaryRed400 }}>
          <img src={StatusErrorIcon} alt='error'></img> {errors}
        </span>
      );
    }

    if (touched && !errors) {
      status = 'success';
      helpText = (
        <span style={{ color: KenColors.tertiaryGreen500 }}>
          <img src={StatusSuccessIcon} alt='success'></img>{' '}
          {t('validations:standard_success')}
        </span>
      );
    }

    if (type === GLOBAL.VALIDATIONS.STATUS.ERROR) {
      return helpText;
    }
    if (type === GLOBAL.VALIDATIONS.TYPE.STATUS) {
      switch (status) {
        case 'warning':
          return 'inputBorderWarning';
        case 'success':
          return 'inputBorderSuccess';

        case 'error':
          return 'inputBorderError';

        default:
          return 'inputBorder';
      }
    }
  };
  return (
    <div>
      <KenTextLabel
        label={label}
        optionalLabel={optionalLabel}
        required={required}
      />
      <StyledToggleButtonGroup
        size={size}
        value={value}
        exclusive
        onChange={(e, newVal) => {
          onChange(newVal);
          setFieldTouched(name, true, false);
        }}
        onBlur={onBlur}
        onFocus={onFocus}
        autoFocus={autofocus}
        type={type}
        style={{ display: 'flex', marginTop: 0 }}
        aria-label='text alignment'
      >
        {options.map((el) => {
          return (
            <ToggleButton
              {...props}
              classes={{ selected: classes.selected }}
              className={classes.toggleButton}
              value={el.value || el}
              aria-label={el.value || value}
              style={{ minWidth: '60px' }}
            >
              {el.label || el}
            </ToggleButton>
          );
        })}
      </StyledToggleButtonGroup>
      <Typography className={classes.typoSupporting} align='left'>
        {getErrorStatus('error')}
      </Typography>
    </div>
  );
}
